import { useState } from 'react'
import { add, compareAsc, intervalToDuration, set } from 'date-fns'
import { useGameInfoAutocomplete, useGiveawayTypes, usePrizeTypes } from '@/hooks/queries'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useDismissable } from '@/hooks/use-dismissable'
import {
  parseDateInput,
  parseTimeInput,
  toDateInputValue,
  toTimeInputValue,
  backgroundImage,
} from '@/lib/format'
import { cn } from '@/lib/cn'
import { type GameInfo } from '@/models/game-info'
import { Dropdown } from '../ui/Dropdown'
import type { StepProps } from './types'

interface BasicsStepProps extends StepProps {
  onEditKeys: () => void
  onAddCustomGame: () => void
  onCancel: () => void
}

const END_PRESETS = [
  { label: '1 Hour', duration: { hours: 1 } },
  { label: '1 Day', duration: { days: 1 } },
  { label: '1 Week', duration: { days: 7 } },
  { label: '1 Month', duration: { days: 28 } },
] as const

/**
 * Port of the step-1 half of components/GiveawayNew.vue: prize type, key count,
 * game search, winner selection and the start/end schedule.
 *
 * The scheduling validators in the original were partly broken — `validateStartTime`
 * referenced an undefined `val`, and the start-date setter compared against
 * `now.getMonth` (the function, not its result). Both are reimplemented here so
 * the invariant they were reaching for actually holds: the start is never in the
 * past, and the end is always after the start.
 */
export function BasicsStep({
  giveaway,
  update,
  onNext,
  onCancel,
  onEditKeys,
  onAddCustomGame,
}: BasicsStepProps) {
  const { data: giveawayTypes = [] } = useGiveawayTypes()
  const { data: prizeTypes = [] } = usePrizeTypes()

  const [scheduleNow, setScheduleNow] = useState(true)
  const [gameQuery, setGameQuery] = useState(giveaway.game_info?.name ?? '')
  const debouncedQuery = useDebouncedValue(gameQuery, 250)
  const { data: gameResults = [] } = useGameInfoAutocomplete(debouncedQuery)
  const gameSearch = useDismissable<HTMLDivElement>()
  const endPresets = useDismissable<HTMLDivElement>()

  const selectedPrizeType = prizeTypes.find((type) => type.id === giveaway.prize_type_id) ?? null
  const requiresKey = selectedPrizeType?.requires_key ?? false
  const keyCount = giveaway.prizes?.length ?? 0

  const lasting = intervalToDuration({ start: giveaway.scheduled_at, end: giveaway.finishes_at })

  /** Keeps `finishes_at` strictly after `scheduled_at`. */
  const withValidEnd = (draft: typeof giveaway) => {
    if (compareAsc(draft.finishes_at, draft.scheduled_at) <= 0) {
      draft.finishes_at = add(draft.scheduled_at, { minutes: 1 })
    }
  }

  const setStartDate = (value: string) => {
    const parsed = parseDateInput(value)
    if (!parsed) return
    update((draft) => {
      const candidate = set(draft.scheduled_at, {
        year: parsed.year,
        month: parsed.month,
        date: parsed.day,
      })
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      draft.scheduled_at = compareAsc(candidate, today) < 0 ? new Date() : candidate
      withValidEnd(draft)
    })
  }

  const setStartTime = (value: string) => {
    const parsed = parseTimeInput(value)
    if (!parsed) return
    update((draft) => {
      const candidate = set(draft.scheduled_at, { hours: parsed.hours, minutes: parsed.minutes })
      draft.scheduled_at = compareAsc(candidate, new Date()) < 0 ? new Date() : candidate
      withValidEnd(draft)
    })
  }

  const setEndDate = (value: string) => {
    const parsed = parseDateInput(value)
    if (!parsed) return
    update((draft) => {
      draft.finishes_at = set(draft.finishes_at, {
        year: parsed.year,
        month: parsed.month,
        date: parsed.day,
      })
      withValidEnd(draft)
    })
  }

  const setEndTime = (value: string) => {
    const parsed = parseTimeInput(value)
    if (!parsed) return
    update((draft) => {
      draft.finishes_at = set(draft.finishes_at, { hours: parsed.hours, minutes: parsed.minutes })
      withValidEnd(draft)
    })
  }

  const selectGame = (game: GameInfo) => {
    setGameQuery(game.name ?? '')
    update((draft) => {
      draft.game_info = game
      draft.game_info_id = game.id
    })
    gameSearch.close()
  }

  return (
    <div className="content-section">
      <div className={cn('field-row', requiresKey && 'with-margin small-margin')}>
        <div className="field half">
          <div className="field-name">Prize Type</div>
          <Dropdown
            label="Prize type"
            icon={<img src="/icons/key.png" width={20} height={20} alt="" />}
            value={giveaway.prize_type_id ?? 0}
            placeholder="Choose a prize type"
            options={prizeTypes.map((type) => ({ value: type.id ?? 0, label: type.name ?? '' }))}
            onChange={(value) =>
              update((draft) => {
                draft.prize_type_id = value
              })
            }
          />
        </div>

        <div className="field half">
          <div className="field-name">How much will you give away?</div>
          {requiresKey ? (
            <button type="button" className="field-box button" onClick={onEditKeys}>
              <div className="field-box-prepend">
                <img src="/icons/stack.png" width={20} height={20} alt="" />
              </div>
              <div className="field-box-content">{keyCount}</div>
              <div className="field-box-append" />
            </button>
          ) : (
            <div className="field-box input">
              <div className="field-box-prepend">
                <img src="/icons/stack.png" width={20} height={20} alt="" />
              </div>
              <input
                className="field-box-input text-center"
                placeholder="Enter amount"
                aria-label="Prize quantity"
                type="number"
                min={1}
                value={keyCount || ''}
                onChange={(event) =>
                  update((draft) => {
                    const count = Math.max(0, Number(event.target.value) || 0)
                    draft.prizes = Array.from({ length: count }, () => '')
                  })
                }
              />
              <div className="field-box-append" />
            </div>
          )}
        </div>
      </div>

      {requiresKey ? (
        <button type="button" className="content-row upper with-margin link" onClick={onEditKeys}>
          {keyCount} Keys Added
        </button>
      ) : null}

      <div className="field-row with-margin small-margin">
        <div className="field">
          <div className="field-name">Game Information</div>
          <div className="field-box input dropdown" ref={gameSearch.ref}>
            <input
              className="field-box-input"
              type="text"
              placeholder="Search game (Optional)"
              aria-label="Search for a game"
              value={gameQuery}
              onChange={(event) => {
                setGameQuery(event.target.value)
                gameSearch.open()
              }}
              onFocus={gameSearch.open}
            />
            <div className="field-box-append button">
              <img src="/icons/search.png" width={20} height={20} alt="" />
            </div>
            <div className={cn('field-dropdown-content', gameSearch.isOpen && 'show')}>
              <div className="field-dropdown-row" onClick={onAddCustomGame}>
                Add Custom Game
              </div>
              {gameResults.map((game) => (
                <div className="field-dropdown-row" key={game.id} onClick={() => selectGame(game)}>
                  {game.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="content-row left with-margin">
        Can&rsquo;t find the game you&rsquo;re looking for?
        <button type="button" className="link" onClick={onAddCustomGame}>
          &nbsp;Add a custom game
        </button>
      </div>

      {giveaway.game_info ? (
        <div className="game-info-row">
          <div
            className="game-info-cover"
            style={backgroundImage(giveaway.game_info.display_cover_media)}
          />
          <div className="game-info-details">
            <div className="game-info-title">{giveaway.game_info.name}</div>
            <div className="game-info-tags">
              {giveaway.game_info.tags.map((tag) => (
                <div className="game-info-tag" key={tag}>
                  {tag}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="field-row">
        <div className="field">
          <div className="field-name">How should a winner be chosen?</div>
          <Dropdown
            label="Winner selection"
            icon={<img src="/icons/gift.png" width={20} height={20} alt="" />}
            value={giveaway.giveaway_type_id}
            options={giveawayTypes.map((type) => ({
              value: type.id ?? 0,
              label: type.name ?? '',
            }))}
            onChange={(value) =>
              update((draft) => {
                draft.giveaway_type_id = value
              })
            }
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field half">
          <div className="field-name full">Schedule the start of your giveaway</div>
          <div className="field-box">
            <div className="field-box-prepend">
              <img src="/icons/date.png" width={20} height={20} alt="" />
            </div>
            <input
              type="date"
              className="field-box-input text-center"
              aria-label="Start date"
              value={toDateInputValue(giveaway.scheduled_at)}
              min={toDateInputValue(new Date())}
              onChange={(event) => {
                setScheduleNow(false)
                setStartDate(event.target.value)
              }}
            />
          </div>
        </div>

        <div className="field half">
          <div className="field-box">
            <div className="field-box-prepend">
              <img src="/icons/time.png" width={20} height={20} alt="" />
            </div>
            {scheduleNow ? (
              <button
                type="button"
                className="field-box-content"
                onClick={() => setScheduleNow(false)}
              >
                Now
              </button>
            ) : (
              <input
                type="time"
                className="field-box-input text-center"
                aria-label="Start time"
                value={toTimeInputValue(giveaway.scheduled_at)}
                onChange={(event) => setStartTime(event.target.value)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="field-row">
        <div className="field half">
          <div className="field-name full">Schedule the end of your giveaway</div>
          <div className="field-box dropdown" ref={endPresets.ref}>
            <div className="field-box-prepend">
              <img src="/icons/date.png" width={20} height={20} alt="" />
            </div>
            <input
              className="field-box-input text-center"
              type="date"
              aria-label="End date"
              value={toDateInputValue(giveaway.finishes_at)}
              min={toDateInputValue(giveaway.scheduled_at)}
              onChange={(event) => setEndDate(event.target.value)}
            />
            <button
              type="button"
              className="field-box-append"
              aria-label="Choose a preset duration"
              onClick={endPresets.toggle}
            >
              <img src="/icons/drop.png" width={13} height={8} alt="" />
            </button>
            <div className={cn('field-dropdown-content', endPresets.isOpen && 'show')}>
              {END_PRESETS.map((preset) => (
                <div
                  className="field-dropdown-row"
                  key={preset.label}
                  onClick={() => {
                    update((draft) => {
                      draft.finishes_at = add(draft.scheduled_at, preset.duration)
                    })
                    endPresets.close()
                  }}
                >
                  {preset.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="field half">
          <div className="field-box input">
            <div className="field-box-prepend">
              <img src="/icons/time.png" width={20} height={20} alt="" />
            </div>
            <input
              className="field-box-input text-center"
              type="time"
              aria-label="End time"
              value={toTimeInputValue(giveaway.finishes_at)}
              onChange={(event) => setEndTime(event.target.value)}
            />
            <div className="field-box-append" />
          </div>
        </div>
      </div>

      <div className="content-row medium text-center">
        Giveaway set to end in
        <span className="highlight">&nbsp;{lasting.days ?? 0} days&nbsp;</span>
        <span className="highlight">{lasting.hours ?? 0} hours&nbsp;</span>and
        <span className="highlight">&nbsp;{lasting.minutes ?? 0} minutes</span>
      </div>
      <div className="content-row small text-center">
        (Winners will be selected at the given time above)
      </div>

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="cta next" onClick={onNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
